import type { TripChangeKind, UndoRedoResponse } from "@/types/action"
import type { ItineraryEvent } from "@/types/itinerary"

export function describeUndoRedoResponse(
  response: UndoRedoResponse,
  direction: UndoRedoDirection
): UndoRedoResponseDescription {
  if (response.status !== "ok") {
    return {
      direction,
      normalizedActions: [],
      toast: undefined,
      warning: response.status === "warn" ? response.error : undefined,
    }
  }

  const normalizedActions = response.actions.map((action) =>
    describeUndoRedoAction(action, direction)
  )
  const lastAction = normalizedActions.at(-1)

  if (response.trip_changes?.length) {
    return {
      direction,
      normalizedActions,
      toast: {
        action: "trip-change",
        message: buildTripChangeMessage(
          direction,
          response.trip_changes,
          normalizedActions.length
        ),
      },
    }
  }

  if (!lastAction) {
    return {
      direction,
      normalizedActions,
      toast: undefined,
    }
  }

  if (normalizedActions.length === 1) {
    return {
      direction,
      normalizedActions,
      toast:
        lastAction.toastAction === "move"
          ? undefined
          : {
              action: lastAction.toastAction,
              event: lastAction.event,
            },
    }
  }

  return {
    direction,
    normalizedActions,
    toast: {
      action: getBulkToastAction(direction),
      event: lastAction.event,
      message: buildUndoRedoBulkMessage(direction, normalizedActions),
    },
  }
}

function describeUndoRedoAction(
  action: UndoRedoResponse["actions"][number],
  direction: UndoRedoDirection
): UndoRedoActionDescription {
  const operation = normalizeUndoRedoOperation(action.operation)
  return {
    direction,
    event: action.event,
    operation,
    rawOperation: action.operation,
    toastAction: getToastAction(operation),
  }
}

function normalizeUndoRedoOperation(
  operation: UndoRedoAppliedActionOperation
): NormalizedUndoRedoOperation {
  if (operation === "added" || operation === "undeleted") {
    return "added"
  }
  if (operation === "deleted") {
    return "deleted"
  }
  if (operation === "moved" || operation === "unmoved") {
    return "moved"
  }
  if (operation === "expanded" || operation === "unexpanded") {
    return "expanded"
  }
  if (operation === "changed" || operation === "unchanged") {
    return "changed"
  }
  return assertNever(operation)
}

function getToastAction(
  operation: NormalizedUndoRedoOperation
): UndoRedoToastAction {
  if (operation === "added") {
    return "add"
  }
  if (operation === "deleted") {
    return "remove"
  }
  if (operation === "moved") {
    return "move"
  }
  if (operation === "expanded") {
    return "expand"
  }
  if (operation === "changed") {
    return "change"
  }
  return assertNever(operation)
}

function getBulkToastAction(direction: UndoRedoDirection): UndoRedoToastAction {
  return direction === "undo" ? "bulk-undo" : "bulk-redo"
}

function buildUndoRedoBulkMessage(
  direction: UndoRedoDirection,
  actions: UndoRedoActionDescription[]
) {
  const total = actions.length
  const addCount = countToastActions(actions, "add")
  const deleteCount = countToastActions(actions, "remove")

  if (total === addCount) {
    return `Added ${total} itinerary events.`
  }
  if (total === deleteCount) {
    return `Removed ${total} itinerary events.`
  }

  const modifiedCount = total - addCount - deleteCount
  const messageParts: string[] = []
  if (addCount > 0) {
    messageParts.push(`${addCount} added`)
  }
  if (deleteCount > 0) {
    messageParts.push(`${deleteCount} removed`)
  }
  if (modifiedCount > 0) {
    messageParts.push(`${modifiedCount} modified`)
  }

  return `${getBulkMessagePrefix(direction)} ${total} itinerary events. (${messageParts.join(", ")})`
}

function buildTripChangeMessage(
  direction: UndoRedoDirection,
  tripChanges: NonNullable<UndoRedoResponse["trip_changes"]>,
  eventChangeCount: number
) {
  const totals = new Map<TripChangeKind, number>()
  for (const { kind, count } of tripChanges) {
    totals.set(kind, (totals.get(kind) ?? 0) + (count ?? 1))
  }
  const parts = [...totals].map(([kind, total]) =>
    describeTripChange(kind, total, direction)
  )
  if (eventChangeCount > 0) {
    const eventSuffix = direction === "undo" ? " undone" : " restored"
    parts.push(
      eventChangeCount === 1
        ? `1 itinerary event change${eventSuffix}`
        : `${eventChangeCount} itinerary event changes${eventSuffix}`
    )
  }
  if (parts.length === 1) {
    return parts[0]
  }
  return `${parts.slice(0, -1).join(", ")} and ${parts.at(-1)}`
}

function describeTripChange(
  kind: TripChangeKind,
  total: number,
  direction: UndoRedoDirection
) {
  const undo = direction === "undo"
  if (kind === "days_added") {
    if (total === 1) {
      return undo ? "Day addition undone" : "Day added again"
    }
    return undo ? `${total} day additions undone` : `${total} days added again`
  }
  if (kind === "days_removed") {
    if (total === 1) {
      return undo ? "Day removal undone" : "Day removed again"
    }
    return undo ? `${total} day removals undone` : `${total} days removed again`
  }
  if (kind === "dates_changed") {
    if (total === 1) {
      return undo ? "Trip date change undone" : "Trip date change restored"
    }
    return undo
      ? `${total} trip date changes undone`
      : `${total} trip date changes restored`
  }
  if (kind === "day_title_changed") {
    if (total === 1) {
      return undo ? "Day title change undone" : "Day title change restored"
    }
    return undo
      ? `${total} day title changes undone`
      : `${total} day title changes restored`
  }
  if (kind === "day_note_changed") {
    if (total === 1) {
      return undo ? "Day note change undone" : "Day note change restored"
    }
    return undo
      ? `${total} day note changes undone`
      : `${total} day note changes restored`
  }
  if (kind === "day_titles_cleared") {
    return undo ? "Day titles restored" : "Day titles cleared again"
  }
  return assertNever(kind)
}

function countToastActions(
  actions: UndoRedoActionDescription[],
  toastAction: UndoRedoToastAction
) {
  return actions.filter((action) => action.toastAction === toastAction).length
}

function getBulkMessagePrefix(direction: UndoRedoDirection) {
  return direction === "undo" ? "Undo" : "Redo"
}

function assertNever(value: never): never {
  throw new Error(`Unexpected undo/redo operation: ${value}`)
}

export type UndoRedoDirection = "undo" | "redo"

export type UndoRedoAppliedActionOperation =
  UndoRedoResponse["actions"][number]["operation"]

export type NormalizedUndoRedoOperation =
  | "added"
  | "deleted"
  | "moved"
  | "expanded"
  | "changed"

export type UndoRedoToastAction =
  | "add"
  | "remove"
  | "move"
  | "expand"
  | "change"
  | "bulk-undo"
  | "bulk-redo"
  | "trip-change"

export type UndoRedoActionDescription = {
  direction: UndoRedoDirection
  event: ItineraryEvent
  operation: NormalizedUndoRedoOperation
  rawOperation: UndoRedoAppliedActionOperation
  toastAction: UndoRedoToastAction
}

export type UndoRedoToastDescription = {
  action: UndoRedoToastAction
  event?: ItineraryEvent
  message?: string
}

export type UndoRedoResponseDescription = {
  direction: UndoRedoDirection
  normalizedActions: UndoRedoActionDescription[]
  toast: UndoRedoToastDescription | undefined
  warning?: string
}
